if (!customElements.get('mood-checker')) {
  customElements.define('mood-checker', class extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.dialog = this.querySelector('[data-mood-dialog]');
      this.panel = this.querySelector('[data-mood-panel]');
      this.firstStep = this.querySelector('[data-mood-first-step]');
      this.secondStep = this.querySelector('[data-mood-second-step]');
      this.submit = this.querySelector('[data-mood-submit]');
      this.shortcut = this.querySelector('[data-mood-shortcut]');
      this.live = this.querySelector('[data-mood-live]');
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

      try {
        this.config = JSON.parse(this.querySelector('[data-mood-checker-config]')?.textContent || '{}');
      } catch (_error) {
        this.config = {};
      }
      this.matches = Array.isArray(this.config.matches) ? this.config.matches : [];
      this.storageKey = this.config.storageKey || this.dataset.storageKey;
      if (!this.dialog || !this.matches.length) return;

      const { signal } = this.abortController;
      this.addEventListener('click', event => this.onClick(event), { signal });
      this.dialog.addEventListener('cancel', event => {
        event.preventDefault();
        this.close();
      }, { signal });
      document.addEventListener('shopify:section:select', event => {
        if (event.detail?.sectionId === this.dataset.sectionId) this.open();
      }, { signal });
      document.addEventListener('shopify:block:select', event => this.selectEditorBlock(event.detail?.blockId), { signal });

      this.restoreProfile();
      if (this.shouldAutoOpen()) this.scheduleAutoOpen();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      clearTimeout(this.openTimer);
    }

    onClick(event) {
      const first = event.target.closest('[data-mood-first]');
      if (first) return this.selectFirst(first.dataset.moodFirst);

      const matchButton = event.target.closest('[data-mood-match]');
      if (matchButton) return this.selectMatch(matchButton.dataset.moodMatch);

      if (event.target.closest('[data-mood-submit]')) return this.saveProfile();
      if (event.target.closest('[data-mood-shortcut]')) return this.open();
      if (event.target === this.dialog || event.target.closest('[data-mood-close]')) return this.close();
    }

    selectFirst(firstKey, focusSecond = true) {
      this.selectedFirstKey = firstKey;
      this.selectedMatch = null;
      this.querySelectorAll('[data-mood-first]').forEach(button => {
        button.setAttribute('aria-pressed', String(button.dataset.moodFirst === firstKey));
      });
      let firstVisibleChoice = null;
      this.querySelectorAll('[data-mood-match]').forEach(button => {
        const visible = button.dataset.moodFirstKey === firstKey;
        button.hidden = !visible;
        button.setAttribute('aria-pressed', 'false');
        if (visible && !firstVisibleChoice) firstVisibleChoice = button;
      });
      this.secondStep.hidden = !firstVisibleChoice;
      this.submit.disabled = true;
      if (focusSecond && firstVisibleChoice) {
        requestAnimationFrame(() => firstVisibleChoice.focus({ preventScroll: true }));
      }
    }

    selectMatch(matchId) {
      const match = this.matches.find(item => item.id === matchId);
      if (!match) return;
      this.selectedMatch = match;
      this.querySelectorAll('[data-mood-match]').forEach(button => {
        button.setAttribute('aria-pressed', String(button.dataset.moodMatch === matchId));
      });
      this.submit.disabled = false;
      this.updateVisual(match);
    }

    updateVisual(match) {
      this.querySelectorAll('[data-mood-image]').forEach(image => {
        image.classList.toggle('is-active', image.dataset.moodImage === match?.id);
      });
      const label = this.querySelector('[data-mood-visual-label]');
      if (label) label.textContent = match ? `${match.firstChoice} · ${match.secondChoice}` : '';
    }

    saveProfile() {
      if (!this.selectedMatch) return;
      this.writeStorage({ id: this.selectedMatch.id, selectedAt: Date.now() });
      try { sessionStorage.removeItem(`${this.storageKey}:dismissed`); } catch (_error) {}
      this.activeProfile = this.selectedMatch;
      this.updateShortcut();
      this.live.textContent = this.getAttribute('data-saved-label') || this.selectedMatch.label;
      document.dispatchEvent(new CustomEvent('mood-profile:changed', { detail: { profile: this.selectedMatch } }));
      this.close(false);
    }

    restoreProfile() {
      const stored = this.readStorage();
      this.activeProfile = this.matches.find(match => match.id === stored?.id) || null;
      if (this.activeProfile) {
        this.selectFirst(this.activeProfile.firstKey, false);
        this.selectMatch(this.activeProfile.id);
      }
      this.updateShortcut();
    }

    updateShortcut() {
      if (!this.shortcut) return;
      this.shortcut.hidden = !this.activeProfile;
      const label = this.shortcut.querySelector('[data-mood-shortcut-label]');
      if (label && this.activeProfile) label.textContent = this.activeProfile.label;
    }

    open() {
      if (!this.dialog || this.dialog.open) return;
      if (this.activeProfile) {
        this.selectFirst(this.activeProfile.firstKey, false);
        this.selectMatch(this.activeProfile.id);
      }
      this.returnFocus = document.activeElement;
      this.dialog.showModal();
      document.body.classList.add('mood-checker-open');
      requestAnimationFrame(() => this.dialog.classList.add('is-open'));
      const focusTarget = this.querySelector('[data-mood-first][aria-pressed="true"]') || this.querySelector('[data-mood-first]');
      focusTarget?.focus({ preventScroll: true });
    }

    async close(rememberDismissal = true) {
      if (!this.dialog.open) return;
      if (rememberDismissal && !this.activeProfile) {
        try { sessionStorage.setItem(`${this.storageKey}:dismissed`, 'true'); } catch (_error) {}
      }
      this.dialog.classList.remove('is-open');
      if (!this.reducedMotion) {
        const animations = this.panel.getAnimations();
        if (animations.length) await Promise.allSettled(animations.map(animation => animation.finished));
      }
      if (this.dialog.open) this.dialog.close();
      document.body.classList.remove('mood-checker-open');
      this.returnFocus?.focus?.({ preventScroll: true });
    }

    selectEditorBlock(blockId) {
      const match = this.matches.find(item => item.id === blockId);
      if (!match) return;
      this.open();
      this.selectFirst(match.firstKey, false);
      this.selectMatch(match.id);
    }

    shouldAutoOpen() {
      if (this.dataset.autoOpen !== 'true' || this.dataset.designMode === 'true' || this.activeProfile) return false;
      try { return sessionStorage.getItem(`${this.storageKey}:dismissed`) !== 'true'; } catch (_error) { return true; }
    }

    scheduleAutoOpen(attempt = 0) {
      clearTimeout(this.openTimer);
      const delay = attempt ? 900 : Math.max(0, Number(this.dataset.delay || 0));
      this.openTimer = setTimeout(() => {
        if (!this.shouldAutoOpen()) return;
        const competingDialog = [...document.querySelectorAll('dialog[open]')].some(dialog => dialog !== this.dialog);
        if (competingDialog && attempt < 8) return this.scheduleAutoOpen(attempt + 1);
        if (!competingDialog) this.open();
      }, delay);
    }

    readStorage() {
      try { return JSON.parse(localStorage.getItem(this.storageKey) || 'null'); } catch (_error) { return null; }
    }

    writeStorage(value) {
      try { localStorage.setItem(this.storageKey, JSON.stringify(value)); } catch (_error) {}
    }
  });
}
